import { type Component, createSignal, createResource, Show, For } from 'solid-js'
import { A, useNavigate } from '@solidjs/router'
import { Icon } from '@iconify-icon/solid'
import { presalePoolDatesToPayload, rawDateToInput, utcDateToInput } from '../domain/presale-pool-dates'
import { AppRoutes } from '../../../shared/app-routes'
import { useAdminEvents } from '../../../shared/providers/coris/coris-admin-events.provider'
import { useUser } from '../../../shared/providers/user-provider'
import { useNotification } from '../../../shared/notifications'
import DateTimeInput from '../../../components/shared/components/DateTimeInput'
import TimezoneSelect from '../../../components/shared/components/TimezoneSelect'

const labelClass = 'text-sm font-medium text-gray-900'

interface DateState {
  readonly tz: string
  readonly start: string
  readonly end: string
  readonly reveal: string
  readonly hide: string
  readonly general: string
}

const AdminPresalePoolEditPage: Component = () => {
  const {
    getPresaleCodePools,
    getPresaleDashboard,
    getEvents,
    assignPoolToEvent,
    unassignPoolFromEvent,
    updatePresalePoolDates
  } = useAdminEvents()
  const { organizationSlug } = useUser()
  const notification = useNotification()
  const navigate = useNavigate()

  const [selectedTimezone, setSelectedTimezone] = createSignal(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [presaleStart, setPresaleStart] = createSignal('')
  const [presaleEnd, setPresaleEnd] = createSignal('')
  const [presaleReveal, setPresaleReveal] = createSignal('')
  const [presaleHide, setPresaleHide] = createSignal('')
  const [generalSale, setGeneralSale] = createSignal('')
  const [eventSearch, setEventSearch] = createSignal('')
  const [eventIds, setEventIds] = createSignal<number[]>([])

  // Hide date mirrors presale end until it holds an explicit value (persisted or user-edited).
  const [hideTouched, setHideTouched] = createSignal(false)
  const changePresaleEnd = (v: string): void => {
    setPresaleEnd(v)
    if (!hideTouched()) setPresaleHide(v)
  }
  const changePresaleHide = (v: string): void => {
    setHideTouched(true)
    setPresaleHide(v)
  }

  // Server-known baseline used to compute the diff on save and to revert on cancel.
  const [originalDates, setOriginalDates] = createSignal<DateState | null>(null)
  const [originalEventIds, setOriginalEventIds] = createSignal<number[]>([])

  const captureOriginal = (next: DateState, ids: number[]): void => {
    setOriginalDates(next)
    setOriginalEventIds(ids)
    setSelectedTimezone(next.tz)
    setPresaleStart(next.start)
    setPresaleEnd(next.end)
    setPresaleReveal(next.reveal)
    setPresaleHide(next.hide)
    setHideTouched(Boolean(next.hide))
    setGeneralSale(next.general)
    setEventIds(ids)
  }

  // Fetch the latest pool (highest ID)
  const [poolData] = createResource(
    () => organizationSlug(),
    async () => {
      const [pools, dashboard] = await Promise.all([getPresaleCodePools(), getPresaleDashboard()])
      const latest = [...pools].sort((a, b) => b.id - a.id)[0] ?? null
      if (latest) {
        const tz = latest.timezone ?? selectedTimezone()
        captureOriginal(
          {
            tz,
            start: rawDateToInput(latest.presale_start),
            end: rawDateToInput(latest.presale_end),
            reveal: utcDateToInput(latest.presale_reveal, tz),
            hide: utcDateToInput(latest.presale_hide, tz),
            general: rawDateToInput(latest.general_sale_start)
          },
          latest.eventIds
        )
      }
      return { pool: latest, stats: dashboard?.pool?.stats ?? null }
    }
  )

  // Fetch all events for assignment
  const [allEvents] = createResource(
    () => organizationSlug(),
    async () => getEvents()
  )

  const pool = () => poolData()?.pool ?? null
  const stats = () => poolData()?.stats ?? null
  const linkedEventIds = () => new Set(eventIds())

  const filteredEvents = () => {
    const q = eventSearch().toLowerCase()
    return (allEvents() ?? []).filter((e) => {
      if (!q) return true
      return (
        e.title.toLowerCase().includes(q) ||
        (e.venue?.name ?? '').toLowerCase().includes(q) ||
        (e.venue?.address?.city ?? '').toLowerCase().includes(q)
      )
    })
  }

  const totalEvents = () => (allEvents() ?? []).length
  const assignedCount = () => linkedEventIds().size

  const toggleEvent = (id: number): void => {
    setEventIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const currentDates = (): DateState => ({
    tz: selectedTimezone(),
    start: presaleStart(),
    end: presaleEnd(),
    reveal: presaleReveal(),
    hide: presaleHide(),
    general: generalSale()
  })

  const datesDirty = (): boolean => {
    const orig = originalDates()
    if (!orig) return false
    const cur = currentDates()
    return (
      cur.tz !== orig.tz ||
      cur.start !== orig.start ||
      cur.end !== orig.end ||
      cur.reveal !== orig.reveal ||
      cur.hide !== orig.hide ||
      cur.general !== orig.general
    )
  }

  const assignmentDiff = (): { toAdd: number[]; toRemove: number[] } => {
    const cur = new Set(eventIds())
    const orig = new Set(originalEventIds())
    const toAdd = [...cur].filter((id) => !orig.has(id))
    const toRemove = [...orig].filter((id) => !cur.has(id))
    return { toAdd, toRemove }
  }

  const hasChanges = (): boolean => {
    if (datesDirty()) return true
    const { toAdd, toRemove } = assignmentDiff()
    return toAdd.length > 0 || toRemove.length > 0
  }

  const [saving, setSaving] = createSignal(false)

  const discard = (): void => {
    const orig = originalDates()
    if (orig) {
      setSelectedTimezone(orig.tz)
      setPresaleStart(orig.start)
      setPresaleEnd(orig.end)
      setPresaleReveal(orig.reveal)
      setPresaleHide(orig.hide)
      setHideTouched(Boolean(orig.hide))
      setGeneralSale(orig.general)
    }
    setEventIds(originalEventIds())
  }

  const save = async (): Promise<void> => {
    const p = pool()
    if (!p) return
    setSaving(true)
    try {
      if (datesDirty()) {
        const tz = selectedTimezone()
        await updatePresalePoolDates(
          p.id,
          presalePoolDatesToPayload(
            {
              presaleStart: presaleStart(),
              presaleEnd: presaleEnd(),
              presaleReveal: presaleReveal(),
              presaleHide: presaleHide(),
              generalSaleStart: generalSale()
            },
            tz
          )
        )
      }

      const { toAdd, toRemove } = assignmentDiff()
      await Promise.all([
        ...toAdd.map((id) => assignPoolToEvent(p.id, id)),
        ...toRemove.map((id) => unassignPoolFromEvent(p.id, id))
      ])

      // Lock in the new server state as the next baseline.
      captureOriginal(currentDates(), eventIds())
      notification.success(<span>Changes saved</span>)
    } catch (e) {
      notification.error(<span>Failed to save: {e instanceof Error ? e.message : 'Unknown error'}</span>)
    } finally {
      setSaving(false)
    }
  }

  // startsAt is a floating calendar date stored at UTC midnight — format in UTC so
  // the day never shifts with the viewer's timezone.
  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

  return (
    <div class="flex w-full h-full flex-col px-6 overflow-hidden">
      <div class="mx-auto w-full max-w-[1280px] flex flex-1 min-h-0 flex-col gap-4 py-4">
        <Show
          when={!poolData.loading && pool()}
          fallback={
            <Show
              when={poolData.loading}
              fallback={
                <div class="flex flex-col items-center gap-4 py-12">
                  <Icon icon="heroicons:ticket" class="text-[3rem] text-gray-300" />
                  <p class="text-lg font-medium text-gray-500">No presale code pool found</p>
                  <p class="text-sm text-gray-400">Create a presale code pool first to manage it here.</p>
                  <A
                    href={AppRoutes.AdminPresalePoolCreate()}
                    class="px-4 py-2.5 bg-primary-600 text-white rounded-md text-sm font-semibold hover:bg-primary-700 transition-colors duration-150"
                  >
                    Create Presale Unique Codes
                  </A>
                </div>
              }
            >
              <div class="flex flex-col items-center gap-3 py-12">
                <div class="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                <span class="text-sm text-gray-500">Loading pool...</span>
              </div>
            </Show>
          }
        >
          {/* Single pool card — pills, title, dates form, assigned events table */}
          <div class="bg-white rounded-lg border border-gray-200 px-5 py-4 flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
            <div class="flex flex-row gap-3 items-center">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <Icon icon="heroicons:ticket" class="text-[1.1rem] text-primary-600" />
                </div>
                <div class="min-w-0">
                  <h1 class="text-base font-bold text-gray-900 truncate leading-tight">{pool()!.name}</h1>
                  <span class="text-xs text-gray-500">Pool #{pool()!.id}</span>
                </div>
              </div>
              <Show when={stats()}>
                <div class="flex flex-wrap items-center gap-2 ml-auto">
                  <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[72px]">
                    <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">
                      {stats()!.total.toLocaleString()}
                    </span>
                    <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Total</span>
                  </div>
                  <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[72px]">
                    <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">
                      {stats()!.available.toLocaleString()}
                    </span>
                    <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Available</span>
                  </div>
                  <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[72px]">
                    <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">
                      {stats()!.reserved.toLocaleString()}
                    </span>
                    <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Reserved</span>
                  </div>
                  <div class="flex flex-col items-center gap-0.5 rounded-md bg-white border border-gray-200 px-3 py-1.5 min-w-[72px]">
                    <span class="text-base font-bold text-gray-900 tabular-nums leading-tight">{assignedCount()}</span>
                    <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Assigned</span>
                  </div>
                </div>
              </Show>
            </div>

            <div class="border-t border-gray-100" />

            {/* Compact dates form: timezone in a slim header row, 4 dates on a single row below. */}
            <div class="flex items-center gap-3">
              <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                Dates
              </span>
              <div class="ml-auto w-full max-w-[260px] min-w-0">
                <TimezoneSelect value={selectedTimezone()} onValueChange={setSelectedTimezone} />
              </div>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <div class="flex flex-col gap-1.5">
                <label class={labelClass}>Presale reveal</label>
                <DateTimeInput
                  value={presaleReveal()}
                  onValueChange={setPresaleReveal}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class={labelClass}>Presale start</label>
                <DateTimeInput
                  value={presaleStart()}
                  onValueChange={setPresaleStart}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class={labelClass}>Presale end</label>
                <DateTimeInput
                  value={presaleEnd()}
                  onValueChange={changePresaleEnd}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class={labelClass}>General sale</label>
                <DateTimeInput
                  value={generalSale()}
                  onValueChange={setGeneralSale}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class={labelClass}>Presale hide</label>
                <DateTimeInput
                  value={presaleHide()}
                  onValueChange={changePresaleHide}
                  mode="datetime"
                  placeholder="Date & time"
                />
              </div>
            </div>

            <div class="border-t border-gray-100" />

            {/* Events table (changes are staged until Save) */}
            <div class="flex items-center justify-between">
              <h2 class="text-base font-bold text-gray-900">Assigned events</h2>
              <span class="text-sm font-semibold text-gray-500">
                {assignedCount()}/{totalEvents()}
              </span>
            </div>
            <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div class="text-gray-400">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
              </div>
              <input
                type="text"
                placeholder="Search events..."
                value={eventSearch()}
                onInput={(e) => setEventSearch(e.currentTarget.value)}
                class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
            </div>
            <div class="flex-1 min-h-0 max-h-[calc(100svh-30rem)] overflow-y-auto flex flex-col gap-1">
              <Show
                when={!allEvents.loading}
                fallback={<div class="py-8 text-center text-sm text-gray-400">Loading events...</div>}
              >
                <Show
                  when={filteredEvents().length > 0}
                  fallback={<div class="py-8 text-center text-sm text-gray-400">No events found.</div>}
                >
                  <For each={filteredEvents()}>
                    {(event) => {
                      const isAssigned = (): boolean => linkedEventIds().has(event.id)
                      return (
                        <div
                          class="p-3 rounded-lg flex items-center gap-3 transition-colors duration-100"
                          classList={{
                            'is-assigned bg-primary-50 hover:bg-primary-100': isAssigned(),
                            'bg-gray-50 hover:bg-gray-100': !isAssigned()
                          }}
                        >
                          <div class="flex-1 min-w-0">
                            <span class="text-sm font-medium text-gray-900 block truncate">{event.title}</span>
                            <span class="text-xs text-gray-500 block truncate">
                              {formatDate(event.startsAt)} · {event.venue?.name ?? ''}
                            </span>
                          </div>
                          <Show
                            when={isAssigned()}
                            fallback={
                              <button
                                type="button"
                                class="shrink-0 px-3 py-1.5 bg-primary-600 text-white rounded-md text-xs font-semibold hover:bg-primary-700 transition-colors duration-150"
                                onClick={() => toggleEvent(event.id)}
                              >
                                Assign
                              </button>
                            }
                          >
                            <button
                              type="button"
                              class="shrink-0 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-md text-xs font-semibold hover:border-red-300 hover:text-red-600 transition-colors duration-150"
                              onClick={() => toggleEvent(event.id)}
                            >
                              Unassign
                            </button>
                          </Show>
                        </div>
                      )
                    }}
                  </For>
                </Show>
              </Show>
            </div>
          </div>

          {/* Footer actions — outside the card */}
          <div class="flex justify-end gap-4 shrink-0">
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center transition-colors duration-150"
              classList={{ 'opacity-50 pointer-events-none': saving() || !hasChanges() }}
              onClick={discard}
            >
              Discard
            </button>
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center transition-colors duration-150"
              onClick={() => navigate(AppRoutes.AdminEvents())}
            >
              Cancel
            </button>
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 rounded-md text-sm font-semibold text-center transition-colors duration-150"
              classList={{
                'bg-primary-600 text-white hover:bg-primary-700 shadow-sm': hasChanges() && !saving(),
                'bg-primary-600 text-white opacity-40 cursor-not-allowed': !hasChanges() || saving()
              }}
              disabled={!hasChanges() || saving()}
              onClick={save}
            >
              {saving() ? 'Saving...' : 'Save'}
            </button>
          </div>
        </Show>
      </div>
    </div>
  )
}

export default AdminPresalePoolEditPage
